import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Flame, Crown, ShoppingBag, Bike } from "lucide-react";
import { useLang } from "@/i18n";
import OrderModal from "@/components/OrderModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["all", "entrees", "plats", "sides", "drinks"];

const MenuSection = () => {
  const { lang, t } = useLang();
  const [dishes, setDishes] = useState([]);
  const [cat, setCat] = useState("all");
  const [orderDish, setOrderDish] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dishes`).then((r) => setDishes(r.data)).catch(() => {});
  }, []);

  const filtered = cat === "all" ? dishes : dishes.filter((d) => d.category === cat);

  return (
    <section id="carte" className="border-t border-[#EBDDC6]/70 bg-[#FFFBF3]" data-testid="menu-section">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="mb-3 font-mono2 text-xs uppercase tracking-[0.3em] text-[#A9761F]">{t.menu.overline}</p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-[#C25E18] sm:text-4xl lg:text-5xl" data-testid="menu-title">
            {t.menu.title}
          </h2>
          <p className="mt-4 text-base text-[#7A6753] sm:text-lg">{t.menu.subtitle}</p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#A9761F]/30 bg-[#F5E9D7] px-4 py-1.5 text-xs text-[#A9761F]" data-testid="menu-partner-strip">
            <Bike size={13} /> {t.menu.partner} <a href="https://noldgo.com/restaurants/c6369716-98f1-4462-a9e4-2b9c24324a10" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#E07A2B] underline-offset-2 hover:underline" data-testid="menu-partner-link">Noldgo.com</a>
          </p>
          <p className="mt-2 text-xs italic text-[#A08B74]" data-testid="menu-takeaway-note">{t.menu.takeawayNote}</p>
        </motion.div>

        <div className="mb-12 flex flex-wrap gap-2" data-testid="menu-categories">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`menu-cat-${c}`}
              className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${cat === c
                ? "border-[#E07A2B] bg-[#E07A2B] text-[#3B2A1E] font-semibold"
                : "border-[#EBDDC6] text-[#7A6753] hover:border-[#E07A2B]/60 hover:text-[#3B2A1E]"}`}>
              {t.menu.cats[c]}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={cat + lang}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45 }}
            className="grid gap-x-12 gap-y-2 md:grid-cols-2"
          >
            {filtered.map((d) => (
              <div key={d.id} className="group flex items-start gap-4 border-b border-[#EBDDC6]/70 py-5" data-testid={`dish-${d.id}`}>
                {d.image && (
                  <img src={d.image} alt={lang === "fr" ? d.name_fr : d.name_nl} loading="lazy"
                    className="h-16 w-16 shrink-0 rounded-lg border border-[#EBDDC6] object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-lg font-semibold text-[#3B2A1E] transition-colors duration-300 group-hover:text-[#E07A2B] sm:text-xl">
                      {lang === "fr" ? d.name_fr : d.name_nl}
                    </h3>
                    <span className="mx-1 hidden flex-1 border-b border-dotted border-[#D9C5A6] sm:block" />
                    <span className="font-mono2 shrink-0 text-sm text-[#A9761F]">{d.price.toFixed(2).replace(".", ",")} €</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-[#A08B74]">{lang === "fr" ? d.desc_fr : d.desc_nl}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {d.signature && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#A9761F]/15 px-2.5 py-0.5 text-[11px] font-medium text-[#A9761F]">
                        <Crown size={11} /> {t.menu.signature}
                      </span>
                    )}
                    {d.spicy && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#E07A2B]/15 px-2.5 py-0.5 text-[11px] font-medium text-[#E07A2B]">
                        <Flame size={11} /> {t.menu.spicy}
                      </span>
                    )}
                    <button onClick={() => setOrderDish(d)} data-testid={`dish-takeaway-${d.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#E07A2B]/50 px-3 py-1 text-[11px] font-semibold text-[#E07A2B] transition-all duration-300 hover:bg-[#E07A2B] hover:text-[#3B2A1E]">
                      <ShoppingBag size={11} /> {t.menu.takeaway} — <span className="font-mono2">{(d.price * 1.25).toFixed(2).replace(".", ",")} €</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {orderDish && <OrderModal dish={orderDish} onClose={() => setOrderDish(null)} />}
      </div>
    </section>
  );
};

export default MenuSection;
